import { getErrorMessageFromErrorType } from '@flowwai-frontend/shared/utils/common'
import {
   ApplicationTemplateProperties,
   PipelineItemType
} from '@flowwai-frontend/bps-officer-web/graphql'
import {
   useApplicationTemplateStore,
   useCustomObjectTemplateStore,
   useDealTemplatesStore,
   useLeadTemplatesStore
} from '@flowwai-frontend/shared/sales-crm/sales-crm-core'

import { TemplateBaseDetailsType } from '../../types'
import { storeApplicationBaseDetailsInTemplateStore } from '../../factories/applicationFactories'
import { storeCustomObjectBaseDetailsInTemplateStore } from '../../factories/customObject'
import { storeDealBaseDetailsInTemplateStore } from '../../factories/deal'
import { storeLeadBaseDetailsInTemplateStore } from '../../factories/lead'

import { GetPipelineItemTemplateDataQuery as Query } from './query.generatedTypes'

interface ResponseArgsType {
   data: Query | undefined

   onFailure: (errorMessage: string, data: Query['getSalesTemplates']) => void

   onSuccess?: (templates: Array<TemplateBaseDetailsType>) => void
}

interface ReturnType {
   templates: Array<TemplateBaseDetailsType>
}

const useAPIResponseHandler = (args: ResponseArgsType): ReturnType => {
   const leadTemplateStore = useLeadTemplatesStore()

   const dealTemplateStore = useDealTemplatesStore()

   const customObjectTemplateStore = useCustomObjectTemplateStore()

   const applicationTemplateStore = useApplicationTemplateStore()

   const templates: Array<TemplateBaseDetailsType> = []

   if (!args.data) {
      return { templates }
   }

   const { getSalesTemplates } = args.data

   switch (getSalesTemplates.__typename) {
      case 'GetSalesTemplateResult': {
         getSalesTemplates.salesTemplates.forEach(template => {
            if (template.__typename === 'PipelineItemTemplate') {
               templates.push({
                  templateName: template.name,
                  templateId: template.pipelineItemTemplateId
               })

               switch (template.pipelineItemType) {
                  case PipelineItemType.Application: {
                     storeApplicationBaseDetailsInTemplateStore(
                        template.pipelineItemTemplateId,
                        template.name,
                        applicationTemplateStore,
                        template.applicationType,
                        (template?.applicationTemplateProperties as ApplicationTemplateProperties) ??
                           undefined
                     )

                     break
                  }

                  case PipelineItemType.CustomObject: {
                     storeCustomObjectBaseDetailsInTemplateStore(
                        template.pipelineItemTemplateId,
                        template.name,
                        customObjectTemplateStore,
                        template.customObjectTemplateProperties ?? undefined
                     )

                     break
                  }

                  case PipelineItemType.Deal: {
                     storeDealBaseDetailsInTemplateStore(
                        template.pipelineItemTemplateId,
                        template.name,
                        dealTemplateStore
                     )

                     break
                  }

                  case PipelineItemType.Lead: {
                     storeLeadBaseDetailsInTemplateStore(
                        template.pipelineItemTemplateId,
                        template.name,
                        leadTemplateStore
                     )

                     break
                  }
               }
            }
         })

         args.onSuccess?.(templates)

         break
      }

      default: {
         const errorType = getSalesTemplates.__typename
         const errorMessage = getErrorMessageFromErrorType(errorType)

         args.onFailure?.(errorMessage, getSalesTemplates)
      }
   }

   return {
      templates
   }
}

export default useAPIResponseHandler
